<script setup lang="ts">
import { addHours, format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ShiftInfoItem, TimeRange } from '@/types';
import { computed, onBeforeMount, ref } from 'vue';
import loaderJSON from '@/static/img/preloader-web-blue.json';
import { Vue3Lottie } from 'vue3-lottie';
import store from '@/store';

interface Props {
  shiftId: string
  timeShift: TimeRange[] | undefined
}

const props = defineProps<Props>()

const isLoading = ref(false)
const error = ref('')
let loaderTimer: ReturnType<typeof setTimeout> | null = null
const shiftInfo = computed(() => {
  return store.getters["data/shiftInfo"];
});

// Находим открытую смену в этом дне
const getTimeZoneById = (array: TimeRange[], id: string) => {
  const item = array.find(obj => obj.id === id);
  return item?.timeZone ?? 0;
};

// Смещение таймзоны из текущей открытой смены
const timeZoneOffset = computed(() => {
  return props.timeShift ? getTimeZoneById(props.timeShift, shiftInfo.value.shiftId) : 0;
});

// Описание типов смен
const getShiftStatus = (shiftStatus: string) => {
  if (!shiftStatus) return false

  switch (shiftStatus) {
    case 'New': return 'Новая';
    case 'Active': return 'Активная';
    case 'Done': return 'Завершенная';
    case 'Miss': return 'Невыход';
    case 'Canceled': return 'Отмененная';
    case 'MissAccepted': return 'Невыход согласован';
  }
}

// Форматирование дат
const dateFormats = {
  date: 'dd.MM.yyyy',
  time: 'HH:mm:ss',
  localTime: 'HH:mm:ss',
} as const;

const formatDateTime = (date: string, formatType: keyof typeof dateFormats, useTimeZoneOffset = true) => {
  if (!date) return;
  const dateObj = new Date(date);
  const adjustedDate = useTimeZoneOffset ? addHours(dateObj, timeZoneOffset.value) : dateObj;
  return format(adjustedDate, dateFormats[formatType], { locale: ru });
};

const formatDate = (date: string) => formatDateTime(date, 'date');
const formatTime = (date: string) => formatDateTime(date, 'time');
const formatLocalTime = (date: string) => formatDateTime(date, 'localTime', false);

const formatShiftDates = (dateFrom: string, dateTo: string) => {
  if (!dateFrom) return
  const from = getDate(dateFrom)
  const to = getDate(dateTo)
  return from === to ? from : `${from} - ${to}`
}

const getFullName = (entry: ShiftInfoItem) => [entry.lastName, entry.firstName, entry.middleName].filter(Boolean).join(' ')

const getDate = (isoDate: string) => {
  const parsedDate = parseISO(isoDate)
  return format(parsedDate, 'dd.MM.yyyy')
}

const getShiftInfo = async () => {
  try {
    // Показываем загрузку если данные не пришли за 200 мс
    loaderTimer = setTimeout(() => {
      isLoading.value = true;
    }, 200)

    if (props.shiftId) {
      await store.dispatch("data/getShiftInfo", props.shiftId)
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (loaderTimer) {
      clearTimeout(loaderTimer)
      loaderTimer = null
    }
    isLoading.value = false;

    // Пишем ошибку если не прошёл запрос (или нет данных) и закончилась загрузка
    if (!isLoading.value && !shiftInfo.value) {
      error.value = `Данные не найдены`
    }
  }
}

onBeforeMount(() => {
  getShiftInfo()
})
</script>

<template>
  <div class="shift-info-layout">
    <template v-if="isLoading">
      <div class="shift-info-preloader">
        <Vue3Lottie class="animate" :animationData="loaderJSON" width="140px" height="140px"></Vue3Lottie>
      </div>
    </template>
    <template v-else-if="shiftInfo">
      <perfect-scrollbar class="shift-info-scroll rail-thin rail-gap-vertical">
        <div class="shift-info-head">
          <div class="shift-info-row">
            <h2>Основная смена</h2>
            <div class="shift-info-status" :class="shiftInfo.shiftStatus">
              {{getShiftStatus(shiftInfo.shiftStatus)}}
            </div>
          </div>
          <div>
            <div class="shift-info-vs">
              <span>Вита Система забрала данные</span>
              <vita-icon name="checked-green" v-if="shiftInfo.isGotVs" />
              <vita-icon name="cross-red" v-else />
            </div>
          </div>
        </div>
        <div class="shift-info-employee">
          {{ getFullName(shiftInfo) }} ({{ shiftInfo.position }} {{ shiftInfo.bid }})
          {{ formatShiftDates(shiftInfo.dateFrom, shiftInfo.dateTo) }}
        </div>
        <table class="shift-info-table">
          <thead>
            <tr>
              <th>Действия по смене</th>
              <th>Значения по смене</th>
              <th>ФИО / должность сотрудника</th>
            </tr>
          </thead>
          <template v-for="(item, index) in shiftInfo.shiftStatusItems" :key="index">
            <tr :class="item.type">
              <td>
                <div class="shift-info-type">{{ item.label }}</div>
                <div class="shift-info-timedate" v-if="item.createdAt">
                  <div>{{ formatLocalTime(item.createdAt) }}</div>
                  <div>{{ formatDate(item.createdAt) }}</div>
                </div>
                <span class="shift-info-timedate" v-else>—</span>
              </td>
              <td>
                <span v-if="item.dateFrom || item.dateTo">
                  с {{ formatTime(item.dateFrom) }} до {{ formatTime(item.dateTo) }}
                </span>
                <span v-else>—</span>
              </td>
              <td><span v-if="getFullName(item)">{{ getFullName(item) }} /</span> {{ item.position }}</td>
            </tr>

            <!-- Если смена отменена - дополнительно отображаем строку с причиной и комментарием -->
            <tr v-if="item.type === 'cancelled'" class="wide-cell">
              <td colspan="3">
                <div>
                  <div class="shift-info-type">Причина отмены:</div>
                  <div class="shift-info-desc">{{ item.cancelationReason || '—' }}</div>
                </div>
                <div class="sub-cell" v-if="item.cancelationComment">
                  <div class="shift-info-type">Комментарий отмены:</div>
                  <div class="shift-info-desc">{{item.cancelationComment}}</div>
                </div>
              </td>
            </tr>
          </template>
        </table>
      </perfect-scrollbar>
    </template>
    <template v-if="error">
      <div class="shift-info-preloader">
        <div>{{ error }}</div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
table {
  border-collapse: collapse;
  width: 100%;
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  letter-spacing: 0;
}

td {
  border: 1px solid #E5E5E5;
  text-align: left;
  padding: 8px 20px;
  height: 51px;
}

tr:not(.wide-cell) {
  background: #FFFFFF;
  
  &.created {
    background: #F4F4F4;
  }

  &.cancelled {
    background: #FFEAEE;
  }

  > td:not(:last-child), > th:not(:last-child) {
    width: 230px;
  }

  > td:last-child {
    word-break: break-word;
  }
}

th {
  border: 1px solid #E5E5E5;
  font-weight: 500;
  padding: 8px 20px;
  text-align: left;
  background: #E9E9E9;
}

.shift-info {
  &-head {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &-layout {
    min-width: 950px;
    min-height: 259px;
  }

  &-preloader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 259px;
  }

  &-status {
    display: inline-block;
    padding: 4px 8px;
    background: #BDBEC1;
    color: #FFFFFF;
    border-radius: 6px;
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;

    &.New {
      background: #5F98EC;
    }

    &.Active {
      background: #68CDBB;
    }

    &.Canceled {
      background: #FD6057;
    }

    &.Miss, &.MissAccepted {
      background: #FF8C5D;
    }
  }

  &-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  &-vs {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0;
    color: #888888;
  }

  &-employee {
    margin-top: 18px;
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    word-break: break-word;
  }

  &-table {
    margin-top: 18px;
  }

  &-type {
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
  }

  &-timedate {
    display: flex;
    align-items: center;
    color: #888888;
    font-weight: 500;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
    margin-top: 4px;

    > div:last-child {
      margin-left: 8px;
      padding-left: 8px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 1px;
        background: #E5E5E5;
      }
    }
  }

  &-scroll {
    overflow: hidden;
    max-height: 601px;
    padding: 30px 60px 60px;
  }

  &-desc {
    font-weight: 500;
    font-size: 13px;
    line-height: 100%;
    letter-spacing: 0;
    color: #888888;
    margin-top: 4px;
  }
}

.sub-cell {
  position: relative;
  padding-top: 8px;
  margin-top: 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -20px;
    right: -20px;
    background: #E5E5E5;
    height: 1px;
  }
}
</style>